import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  validateUsername,
  getUsernameInputProps,
} from "../../utils/usernameValidation";
import {
  validateDisplayName,
  getDisplayNameInputProps,
} from "../../utils/displayNameValidation";
import "./ProfilePage.css";

const ProfilePage: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: user?.displayName || "",
    username: user?.username || "",
    bio: user?.bio || "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h2>Profile</h2>
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (error) setError("");

    // Validate username in real-time
    if (name === "username") {
      const validation = validateUsername(value);
      if (!validation.isValid) {
        setUsernameError(validation.error || "Invalid username");
      } else {
        setUsernameError("");
      }
    }

    // Validate display name in real-time
    if (name === "displayName") {
      const validation = validateDisplayName(value);
      if (!validation.error) {
        setDisplayNameError("");
      } else {
        setDisplayNameError(validation.error);
      }
    }
  };

  const handleSave = async () => {
    // Check for validation errors
    if (usernameError || displayNameError) {
      setError("Please fix validation errors before saving");
      return;
    }

    setLoading(true);
    setMessage("");
    setError("");

    try {
      await updateUserProfile({
        displayName: formData.displayName,
        username: formData.username,
        bio: formData.bio,
      });
      setIsEditing(false);
      setMessage("Profile updated successfully!");
      setUsernameError(""); // Clear username error on success
      setDisplayNameError(""); // Clear display name error on success
    } catch (error: any) {
      setError(error.message || "Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      displayName: user.displayName || "",
      username: user.username || "",
      bio: user.bio || "",
    });
    setIsEditing(false);
    setMessage("");
    setUsernameError("");
    setDisplayNameError("");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleDisplayName = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getSubscriptionDisplayName = (tier: string) => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h2>Profile</h2>
          {!isEditing && (
            <button className="edit-button" onClick={() => setIsEditing(true)}>
              Edit Profile
            </button>
          )}
        </div>

        {message && (
          <div
            className={`message ${
              message.includes("successfully") ? "success" : "error"
            }`}
          >
            {message}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="profile-content">
          <div className="profile-section">
            <h3>Basic Information</h3>

            {isEditing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label htmlFor="displayName">Display Name</label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    placeholder="Enter display name"
                    {...getDisplayNameInputProps()}
                  />
                  {displayNameError && (
                    <div className="error-message display-name-error">
                      {displayNameError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    {...getUsernameInputProps()}
                  />
                  {usernameError && (
                    <div className="error-message username-error">
                      {usernameError}
                    </div>
                  )}
                  {user.usernameLastChangedAt && (
                    <div className="username-info">
                      <small>
                        Last changed: {formatDate(user.usernameLastChangedAt)}
                        {(() => {
                          const lastChangeDate = new Date(
                            user.usernameLastChangedAt
                          );
                          const threeDaysAgo = new Date();
                          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

                          if (lastChangeDate > threeDaysAgo) {
                            const daysRemaining = Math.ceil(
                              (lastChangeDate.getTime() -
                                threeDaysAgo.getTime()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return ` • Can change again in ${daysRemaining} day${
                              daysRemaining !== 1 ? "s" : ""
                            }`;
                          }
                          return " • Can be changed now";
                        })()}
                      </small>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself..."
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    className="cancel-button"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-info">
                <div className="info-row">
                  <span className="label">Display Name:</span>
                  <span className="value">{user.displayName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Username:</span>
                  <span className="value">@{user.username}</span>
                  {user.usernameLastChangedAt && (
                    <small className="username-change-info">
                      Changed {formatDate(user.usernameLastChangedAt)}
                    </small>
                  )}
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{user.email}</span>
                </div>
                {user.bio && (
                  <div className="info-row">
                    <span className="label">Bio:</span>
                    <span className="value">{user.bio}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="profile-section">
            <h3>Account Information</h3>
            <div className="profile-info">
              <div className="info-row">
                <span className="label">Role:</span>
                <span className="value">{getRoleDisplayName(user.role)}</span>
              </div>
              <div className="info-row">
                <span className="label">Subscription:</span>
                <span className="value">
                  {getSubscriptionDisplayName(user.subscriptionTier)}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Member Since:</span>
                <span className="value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span
                  className={`value status ${
                    user.isApproved ? "approved" : "pending"
                  }`}
                >
                  {user.isApproved ? "Approved" : "Pending Approval"}
                </span>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h3>Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{user.mapsCount}</span>
                <span className="stat-label">Maps Created</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{user.followersCount}</span>
                <span className="stat-label">Followers</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{user.followingCount}</span>
                <span className="stat-label">Following</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
